import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, ArrowLeft, ShoppingBag, Send, Mail } from 'lucide-react';
import '../styles/checkout.css';
import { useCarrito } from '../context/carritoContext';
import { calculateCartSummary } from '../services/catalogApi';
import negocioData from '../data/negocioData';
import Seo from './Seo';

const moneyFormatter = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
});

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const limpiarTelefono = (telefono) => String(telefono || '').replace(/\D/g, '');

const buildOrderMessage = ({ negocio, cliente, carrito, subtotal, envio, total }) => {
  const lineas = carrito.map((item) => {
    const precioUnitario = Math.max(0, toNumber(item.artPrecio) - toNumber(item.artDescuento));
    const cantidad = toNumber(item.cantidad) || 1;
    const totalLinea = precioUnitario * cantidad;

    return `${cantidad} x ${item.artNom} - ${moneyFormatter.format(totalLinea)}`;
  });

  return [
    `Hola, soy ${cliente.nombre} de ${cliente.ciudad}.`,
    'Quiero hacer un pedido.',
    '',
    'Resumen del pedido:',
    ...lineas.map((linea) => `- ${linea}`),
    '',
    `Subtotal: ${moneyFormatter.format(subtotal)}`,
    `Envio: ${envio === 0 ? 'Gratis' : moneyFormatter.format(envio)}`,
    `Total: ${moneyFormatter.format(total)}`,
    '',
    `Telefono de contacto: ${cliente.telefono}`,
  ].join('\n');
};

const Checkout = () => {
  const navigate = useNavigate();
  const { carrito } = useCarrito();
  const [resumen, setResumen] = useState({ subtotal: 0, envio: 0 });
  const [modalAbierto, setModalAbierto] = useState(false);
  const [canalEnvio, setCanalEnvio] = useState('whatsapp');
  const [formulario, setFormulario] = useState({
    telefono: '',
    nombre: '',
    ciudad: '',
  });

  const carritoVacio = carrito.length === 0;

  useEffect(() => {
    calculateCartSummary(carrito)
      .then((data) => {
        setResumen({
          subtotal: toNumber(data?.subtotal),
          envio: toNumber(data?.envio),
        });
      })
      .catch((err) => console.error(err));
  }, [carrito]);

  useEffect(() => {
    if (!modalAbierto) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setModalAbierto(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [modalAbierto]);

  const total = resumen.subtotal + resumen.envio;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormulario((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const mensaje = buildOrderMessage({
      negocio: negocioData,
      cliente: formulario,
      carrito,
      subtotal: resumen.subtotal,
      envio: resumen.envio,
      total,
    });

    if (canalEnvio === 'email') {
      const emailDestino = String(negocioData.email || '').trim();
      if (!emailDestino) return;

      const subject = `Nuevo pedido de ${formulario.nombre}`;
      const body = `${mensaje}\n\nContacto del cliente:\n- Telefono: ${formulario.telefono}`;
      const mailtoUrl = `mailto:${encodeURIComponent(emailDestino)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoUrl;
      setModalAbierto(false);
      return;
    }

    const telefonoDestino = limpiarTelefono(negocioData.telefonoWhatsapp);
    if (!telefonoDestino) return;

    const url = `https://wa.me/${telefonoDestino}?text=${encodeURIComponent(mensaje)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setModalAbierto(false);
  };

  if (carritoVacio) {
    return (
      <section className="checkout-page checkout-empty">
        <Seo title="Checkout" path="/checkout" noindex />
        <div className="checkout-hero">
          <div className="checkout-hero-copy">
            <span className="checkout-kicker">Checkout</span>
            <h1>No hay productos para continuar</h1>
            <p>Agrega productos al carrito para ver el resumen y enviar tu pedido por WhatsApp o correo.</p>
            <button className="checkout-back-button" onClick={() => navigate('/tienda')}>
              <ArrowLeft size={16} />
              Ir a tienda
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="checkout-page">
      <Seo title="Confirma tu pedido" path="/checkout" noindex />
      <header className="checkout-hero">
        <div className="checkout-hero-copy">
          <span className="checkout-kicker">Checkout</span>
          <h1>Confirma tu pedido</h1>
          <p>Revisa los articulos, verifica el total y elige si quieres enviarlo por WhatsApp o por correo.</p>
        </div>
      </header>

      <div className="checkout-grid">
        <main className="checkout-main">
          <section className="checkout-panel">
            <div className="checkout-note">
              <div className="checkout-note-icon">
                <ShoppingBag size={18} />
              </div>
              <div>
                <h2>Revisa antes de enviar</h2>
                <p>Este resumen se arma con los productos actuales del carrito y se actualiza al instante.</p>
              </div>
            </div>

            <div className="checkout-panel-header">
              <h2>
                <ShoppingBag size={18} />
                Productos
              </h2>
              <span>{carrito.length} articulos</span>
            </div>

            <ul className="checkout-items">
              {carrito.map((item) => {
                const cantidad = toNumber(item.cantidad) || 1;
                const precioUnitario = Math.max(0, toNumber(item.artPrecio) - toNumber(item.artDescuento));
                const totalLinea = precioUnitario * cantidad;

                return (
                  <li key={item.artId} className="checkout-item">
                    <img
                      src={item.artFoto ? `${item.artFoto}` : '/img/default.jpg'}
                      alt={item.artNom}
                      className="checkout-item-image"
                    />

                    <div className="checkout-item-info">
                      <h3>{item.artNom}</h3>
                      <p>{cantidad} unidad{cantidad > 1 ? 'es' : ''}</p>
                    </div>

                    <div className="checkout-item-price">
                      <span>{moneyFormatter.format(totalLinea)}</span>
                      <small>{moneyFormatter.format(precioUnitario)} c/u</small>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        </main>

        <aside className="checkout-sidebar">
          <section className="checkout-summary">
            <h2>Pedido</h2>

            <div className="checkout-row">
              <span>Subtotal</span>
              <strong>{moneyFormatter.format(resumen.subtotal)}</strong>
            </div>

            <div className="checkout-row">
              <span>Envio</span>
              <strong>{resumen.envio === 0 ? 'Gratis' : moneyFormatter.format(resumen.envio)}</strong>
            </div>

            <hr />

            <div className="checkout-row checkout-total">
              <span>Total</span>
              <strong>{moneyFormatter.format(total)}</strong>
            </div>

            <p className="checkout-disponibilidad">Todos los productos están sujetos a disponibilidad.</p>
          </section>

          <section className="checkout-channel-card">
            <h3>Enviar por</h3>

            <button className="checkout-whatsapp-button" onClick={() => { setCanalEnvio('whatsapp'); setModalAbierto(true); }}>
              <Send size={16} />
              WhatsApp
            </button>

            <button className="checkout-email-button" onClick={() => { setCanalEnvio('email'); setModalAbierto(true); }}>
              <Mail size={16} />
              Correo
            </button>
          </section>

          <button className="checkout-secondary-button" onClick={() => navigate('/carrito')}>
            <ArrowLeft size={16} />
            Volver al carrito
          </button>
        </aside>
      </div>

      {modalAbierto && (
        <div className="checkout-modal-overlay" onClick={() => setModalAbierto(false)}>
          <div
            className="checkout-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Enviar pedido por WhatsApp"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="checkout-modal-header">
              {canalEnvio === 'email' ? <Mail size={22} /> : <MessageCircle size={22} />}
              <h3>{canalEnvio === 'email' ? 'Datos para correo' : 'Datos para WhatsApp'}</h3>
            </div>

            <p>
              Completa tus datos y te llevamos al {canalEnvio === 'email' ? 'correo' : 'chat'} con el pedido listo para enviar.
            </p>

            <form className="checkout-form" onSubmit={handleSubmit}>
              <label>
                Telefono
                <input
                  type="tel"
                  name="telefono"
                  value={formulario.telefono}
                  onChange={handleChange}
                  placeholder="Tu telefono"
                  required
                />
              </label>

              <label>
                Nombre
                <input
                  type="text"
                  name="nombre"
                  value={formulario.nombre}
                  onChange={handleChange}
                  placeholder="Tu nombre"
                  required
                />
              </label>

              <label>
                Ciudad
                <input
                  type="text"
                  name="ciudad"
                  value={formulario.ciudad}
                  onChange={handleChange}
                  placeholder="Tu ciudad"
                  required
                />
              </label>

              <div className="checkout-modal-actions">
                <button type="button" className="checkout-modal-cancel" onClick={() => setModalAbierto(false)}>
                  Cancelar
                </button>
                <button type="submit" className="checkout-modal-submit">
                  {canalEnvio === 'email' ? <Mail size={16} /> : <Send size={16} />}
                  {canalEnvio === 'email' ? 'Abrir correo' : 'Abrir WhatsApp'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default Checkout;
